import {Component, inject, OnDestroy, OnInit} from "@angular/core";
import {MatDialog} from "@angular/material/dialog";
import {Store} from "@ngrx/store";
import {FormBuilder, FormControl, FormGroupDirective, NgForm, Validators} from "@angular/forms";
import {ErrorStateMatcher} from "@angular/material/core";
import {first, map} from "rxjs/operators";
import {Subscription} from "rxjs";
import {
    selectAssetDescription,
    selectGeneratedAssetDescription,
    selectGptAssetDescriptionIsEditable,
    selectIsAnyAssetFormInEditMode,
    selectSelectedAsset
} from "../../store/asset/asset.selectors";
import {AssetDescriptionActions} from "../../store/asset.actions";
import {TraceableText} from "../../../shared/model/traceable";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {WebsiteSearchDialogComponent, WebsiteSearchDialogData} from "../../../shared/components/website-search-dialog/website-search-dialog.component";

class ShowOnDirtyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return !!(control && control.invalid && (control.dirty || control.touched));
    }
}

@Component({
    selector: "valumize-asset-description",
    templateUrl: "./asset-description.component.html",
    styleUrls: ["./asset-description.component.scss"],
    standalone: false
})
export class AssetDescriptionComponent implements OnInit, OnDestroy {

    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);

    subscriptions: Subscription[] = [];

    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    gptDescription$ = this.store.select(selectGeneratedAssetDescription);
    urlErrorMatcher = new ShowOnDirtyErrorStateMatcher();
    isGptEditMode = false;
    gptErrorMessage = "";

    description: { description: TraceableText; url: TraceableText; assetDescriptionIsEditable: boolean } = {
        description: {source: "", modDate: undefined, text: ""},
        url: {source: "", modDate: undefined, text: ""},
        assetDescriptionIsEditable: false
    };

    descriptionForm = this.formBuilder.group({
        assetDescription: this.formBuilder.control("", {nonNullable: true}),
        assetUrl: this.formBuilder.control("", {
            nonNullable: true,
            validators: Validators.pattern(/^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}([/?].*)?$/)
        }),
    });

    constructor() {
    }

    ngOnInit() {
        this.subscriptions.push(this.store.select(selectAssetDescription).pipe(map((description) => {
            this.description = description;
            this.descriptionForm.patchValue({
                assetDescription: description.description?.text,
                assetUrl: description.url?.text
            });
            if (description.assetDescriptionIsEditable) {
                this.descriptionForm.enable();
            } else {
                this.descriptionForm.disable();
            }
        })).subscribe());

        this.subscriptions.push(this.store.select(selectGptAssetDescriptionIsEditable).pipe(map(isGptEditable => {
            this.isGptEditMode = isGptEditable;
        })).subscribe());

        this.subscriptions.push(this.gptDescription$.pipe(map(gptDescription => {
            if (gptDescription.status === "ERROR") {
                this.gptErrorMessage = gptDescription.errorMessage;
                this.isGptEditMode = false;
            }
        })).subscribe());
    }

    editMode = () => this.store.dispatch(AssetDescriptionActions.edit());

    save() {
        if (this.descriptionForm.valid) {
            this.store.dispatch(AssetDescriptionActions.save(this.descriptionForm.getRawValue()));
        }
    }

    cancel = () => this.store.dispatch(AssetDescriptionActions.cancel());

    validateUrl(address?: string): string {
        return address ? (address.startsWith("http://") || address.startsWith("https://") ? address : "https://" + address) : "";
    }

    gptEditMode = () => this.store.dispatch(AssetDescriptionActions.gptEdit());

    gptCancel = () => this.store.dispatch(AssetDescriptionActions.gptCancel());

    get hasWebsiteUrl(): boolean {
        return !!(this.descriptionForm.controls.assetUrl.value?.trim());
    }

    openConfirmDialog(copyOrAddGptDescription: () => void) {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `Generated text can be factually wrong, please check important information.`;

        dialogRef.afterClosed().pipe(map(result => {
            if (result) {
                copyOrAddGptDescription();
            }
        })).subscribe();
    }

    copyGptDescription(gptDescription: TraceableText) {
        this.openConfirmDialog(() => {
            if (gptDescription.text) {
                this.descriptionForm.patchValue({assetDescription: gptDescription.text});
            }
        });
    }

    addGptDescription(gptDescription: TraceableText) {
        this.openConfirmDialog(() => {
            if (gptDescription.text) {
                this.descriptionForm.patchValue({assetDescription: `${this.descriptionForm.value.assetDescription}\n\n${gptDescription.text}`});
            }
        });
    }

    searchWebsite(): void {
        this.subscriptions.push(
            this.store.select(selectSelectedAsset).pipe(first()).subscribe(asset => {
                const companyName = asset.data.name?.text || "";
                if (!companyName) {
                    return;
                }

                const dialogData: WebsiteSearchDialogData = {companyName};
                const countryCode = asset.data.country?.code;
                if (countryCode) {
                    dialogData.country = countryCode;
                }

                const dialogRef = this.dialog.open(WebsiteSearchDialogComponent, {
                    width: "600px",
                    data: dialogData
                });

                this.subscriptions.push(dialogRef.afterClosed().subscribe(url => {
                    if (url) {
                        this.descriptionForm.patchValue({assetUrl: url});
                        this.descriptionForm.controls.assetUrl.markAsDirty();
                    }
                }));
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
